import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axiosInstance from '../../apiServices/axios';
import { PULSE_ME } from '../../apiServices/api_routes';

const PulseModalContext = createContext(null);

const SESSION_SHOWN_KEY = 'lens_pulse_modal_shown';

export function markPulseModalShownForSession() {
    try {
        sessionStorage.setItem(SESSION_SHOWN_KEY, '1');
    } catch (e) {
        // ignore
    }
}

export function hasPulseModalBeenShownThisSession() {
    try {
        return sessionStorage.getItem(SESSION_SHOWN_KEY) === '1';
    } catch (e) {
        return false;
    }
}

export function clearPulseModalSessionFlag() {
    try {
        sessionStorage.removeItem(SESSION_SHOWN_KEY);
    } catch (e) {
        // ignore
    }
}

function resolveWorkspaceId(activeWorkspaceId) {
    return activeWorkspaceId || localStorage.getItem('active_workspace_id') || '';
}

function extractErrorMessage(error) {
    const data = error?.response?.data;
    if (!data) {
        return error?.message || 'Failed to load Lens Pulse.';
    }
    if (typeof data.detail === 'string') {
        return data.detail;
    }
    if (typeof data.message === 'string') {
        return data.message;
    }
    if (Array.isArray(data.workspace_id) && data.workspace_id[0]) {
        return String(data.workspace_id[0]);
    }
    return 'Failed to load Lens Pulse.';
}

export const PulseModalProvider = ({ children }) => {
    const activeWorkspaceIdFromState = useSelector(state => state?.user?.activeWorkspaceId);
    const activeWorkspaceId = resolveWorkspaceId(activeWorkspaceIdFromState);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [rolloutEnabled, setRolloutEnabled] = useState(false);
    const [pulseData, setPulseData] = useState(null);
    const [message, setMessage] = useState('');
    const fetchSeq = useRef(0);

    React.useEffect(() => {
        if (!activeWorkspaceId) {
            setRolloutEnabled(false);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const { data } = await axiosInstance.get(PULSE_ME, {
                    params: { workspace_id: activeWorkspaceId },
                });
                if (!cancelled) {
                    setRolloutEnabled(Boolean(data?.rollout_enabled));
                }
            } catch (e) {
                if (!cancelled) {
                    setRolloutEnabled(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [activeWorkspaceId]);

    const fetchPulse = useCallback(
        async ({ refresh = false } = {}) => {
            if (!activeWorkspaceId) {
                setRolloutEnabled(false);
                setPulseData(null);
                setMessage('');
                return null;
            }

            const seq = ++fetchSeq.current;
            if (refresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            try {
                const response = refresh
                    ? await axiosInstance.post(PULSE_ME, { workspace_id: activeWorkspaceId })
                    : await axiosInstance.get(PULSE_ME, { params: { workspace_id: activeWorkspaceId } });

                if (seq !== fetchSeq.current) {
                    return null;
                }

                const data = response?.data || {};
                setRolloutEnabled(Boolean(data.rollout_enabled));
                if (data.run) {
                    setPulseData(data.run);
                } else if (!refresh) {
                    setPulseData(null);
                }
                setMessage(data.message || '');

                if (refresh && data.refresh_failed && data.message) {
                    toast.warn(data.message);
                } else if (refresh && data.message && !data.run) {
                    toast.warn(data.message);
                }

                return data;
            } catch (error) {
                if (seq !== fetchSeq.current) {
                    return null;
                }
                const errorMessage = extractErrorMessage(error);
                setMessage(errorMessage);
                if (!refresh) {
                    setPulseData(null);
                }
                if (refresh) {
                    toast.error(errorMessage);
                }
                return null;
            } finally {
                if (seq === fetchSeq.current) {
                    setLoading(false);
                    setRefreshing(false);
                }
            }
        },
        [activeWorkspaceId]
    );

    const openModal = useCallback(async () => {
        setOpen(true);
        await fetchPulse();
    }, [fetchPulse]);

    const closeModal = useCallback(() => {
        setOpen(false);
    }, []);

    const refreshPulse = useCallback(async () => {
        return fetchPulse({ refresh: true });
    }, [fetchPulse]);

    const tryAutoOpenOnProjects = useCallback(async () => {
        if (hasPulseModalBeenShownThisSession() || !activeWorkspaceId) {
            return;
        }
        const data = await fetchPulse();
        if (data?.rollout_enabled) {
            setOpen(true);
            markPulseModalShownForSession();
        }
    }, [activeWorkspaceId, fetchPulse]);

    const value = useMemo(
        () => ({
            open,
            loading,
            refreshing,
            rolloutEnabled,
            pulseData,
            message,
            openModal,
            closeModal,
            refreshPulse,
            fetchPulse,
            tryAutoOpenOnProjects,
        }),
        [open, loading, refreshing, rolloutEnabled, pulseData, message, openModal, closeModal, refreshPulse, fetchPulse, tryAutoOpenOnProjects]
    );

    return <PulseModalContext.Provider value={value}>{children}</PulseModalContext.Provider>;
};

export function usePulseModal() {
    const ctx = useContext(PulseModalContext);
    if (!ctx) {
        throw new Error('usePulseModal must be used within PulseModalProvider');
    }
    return ctx;
}

export function usePulseModalOptional() {
    return useContext(PulseModalContext);
}
